import { useMemo, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  Artifact,
  fetchWorkflowArtifacts,
} from '@/services/logicAppsApi';
import type { Workflow } from '@/types/logicApps';
import {
  getEmbeddedPublishedDefinition,
  normalizeWorkflowId,
} from '@/utils/workflow/workflowDependencyGraph';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const haveSameDefinitions = (
  current: ReadonlyMap<string, Record<string, unknown>>,
  next: ReadonlyMap<string, Record<string, unknown>>,
): boolean =>
  current.size === next.size &&
  Array.from(next).every(([workflowId, definition]) =>
    Object.is(current.get(workflowId), definition),
  );

export const workflowGraphDefinitionQueryPrefix = (
  projectName?: string,
  appName?: string,
) => [
  'workflowGraphDefinition',
  projectName?.toLowerCase(),
  appName?.toLowerCase(),
] as const;

export const workflowGraphDefinitionQueryKey = (
  projectName: string,
  appName: string,
  workflow: Workflow,
) => [
  ...workflowGraphDefinitionQueryPrefix(projectName, appName),
  normalizeWorkflowId(workflow.id),
  workflow.properties.changedTime ?? null,
] as const;

export interface WorkflowGraphDefinitionsResult {
  definitions: ReadonlyMap<string, Record<string, unknown>>;
  isLoading: boolean;
  failedCount: number;
}

export const useWorkflowGraphDefinitions = (
  workflows: readonly Workflow[],
  projectName: string | undefined,
  appName: string | undefined,
): WorkflowGraphDefinitionsResult => {
  const { getOttoToken } = useAuth();
  const missingDefinitions = useMemo(
    () => workflows.filter((workflow) => !getEmbeddedPublishedDefinition(workflow)),
    [workflows],
  );

  const queries = useQueries({
    queries: missingDefinitions.map((workflow) => ({
      queryKey: workflowGraphDefinitionQueryKey(
        projectName ?? '',
        appName ?? '',
        workflow,
      ),
      queryFn: async (): Promise<Record<string, unknown> | undefined> => {
        if (!projectName || !appName) return undefined;
        const { files } = await fetchWorkflowArtifacts({
          projectName,
          appName,
          getToken: getOttoToken,
          workflowName: workflow.name.split('/').at(-1) ?? workflow.name,
          includeDraft: false,
        });
        const published = files?.[Artifact.WorkflowFile];
        if (!published) return undefined;
        return isRecord(published.definition)
          ? published.definition
          : isRecord(published)
            ? published
            : undefined;
      },
      enabled: !!projectName && !!appName,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const definitionsRef = useRef<
    ReadonlyMap<string, Record<string, unknown>>
  >(new Map());

  const definitions = useMemo(() => {
    const result = new Map<string, Record<string, unknown>>();
    for (const workflow of workflows) {
      const embedded = getEmbeddedPublishedDefinition(workflow);
      if (embedded) result.set(workflow.id, embedded);
    }
    queries.forEach((query, index) => {
      const workflow = missingDefinitions[index];
      if (workflow && query.data) result.set(workflow.id, query.data);
    });
    if (haveSameDefinitions(definitionsRef.current, result)) {
      return definitionsRef.current;
    }
    definitionsRef.current = result;
    return result;
  }, [missingDefinitions, queries, workflows]);

  return {
    definitions,
    isLoading: queries.some((query) => query.isLoading),
    failedCount: queries.filter((query) => query.isError).length,
  };
};
